/**
 * Som do aviso de mensagem nova.
 *
 * Numa oficina o celular fica no bolso do macacão e o computador no balcão, com
 * barulho de compressor em volta. Aviso só visual não chega: a pessoa descobre
 * o cliente esperando quando abre a tela, meia hora depois.
 *
 * É sintetizado com WebAudio em vez de tocar um arquivo: não há download, não
 * há arquivo para sumir do cache, e funciona igual em todo navegador moderno.
 *
 * O navegador só deixa tocar depois de a pessoa ter interagido com a página.
 * Por isso `liberarSom()` é chamado no primeiro toque/clique — sem ele, o
 * primeiro aviso sairia mudo e ninguém entenderia por quê.
 */

let contexto: AudioContext | null = null;

function obterContexto(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  contexto ??= new Ctor();
  return contexto;
}

/** Chamado no primeiro gesto da pessoa, para o navegador liberar o áudio. */
export function liberarSom(): void {
  const ctx = obterContexto();
  if (ctx && ctx.state === 'suspended') void ctx.resume();
}

/**
 * Duas notas curtas, subindo. Curto de propósito: som longo numa loja com
 * movimento vira incômodo, e incômodo termina desligado.
 */
export function tocarAvisoDeMensagem(): void {
  const ctx = obterContexto();
  if (!ctx || ctx.state !== 'running') return;

  const agora = ctx.currentTime;
  for (const [i, hz] of [880, 1175].entries()) {
    const osc = ctx.createOscillator();
    const ganho = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = hz;

    const inicio = agora + i * 0.12;
    ganho.gain.setValueAtTime(0, inicio);
    ganho.gain.linearRampToValueAtTime(0.18, inicio + 0.015);
    ganho.gain.exponentialRampToValueAtTime(0.0001, inicio + 0.11);

    osc.connect(ganho).connect(ctx.destination);
    osc.start(inicio);
    osc.stop(inicio + 0.13);
  }
}

const CHAVE = 'gl:som-do-aviso';

export function somLigado(): boolean {
  try {
    return localStorage.getItem(CHAVE) !== 'off';
  } catch {
    // Janela anônima ou armazenamento bloqueado: o padrão é tocar.
    return true;
  }
}

export function definirSom(ligado: boolean): void {
  try {
    localStorage.setItem(CHAVE, ligado ? 'on' : 'off');
  } catch {
    // Não poder lembrar a preferência não pode quebrar a tela.
  }
}
